// uiarc: date-range-picker (https://uiarc.dev/r/date-range-picker.json) — CSS Modules yerine Tailwind.
// Tetikleyicinin kutusu açılan panele dönüşür (tek yüzey: genişlik, yükseklik, köşe ve kayma yaylarla), panel iki
// ay + hazır aralık rayı (dar yerde tek ay + kayan sıra) gösterir. Aralık vurgusu her hafta için bir çubuk artı iki
// uç diskten oluşur; üzerine gelince ya da klavyeyle gezince uzar, uçlar günden güne süzülür, aylar gidilen yöne
// kayar. "Uygula"da etiket tetikleyicide yerinde döner (panelden uçmaz; uçuş yüzeyin dışında görünüyordu).
// Sapmalar: API proje sözleşmesi (`from`/`to` `YYYY-MM-DD`, `withTime`, `defaultRange`, `idPrefix`); hazır
// aralıklar `lib/date-range.ts`, "Tümü" aralığı tarihsiz taslaktır; gün başı Pazartesi; saat seçimi `ui/select`
// (taşınmış açılır liste: dışarı tıklama/odak denetimi onu da kapsar); vurgu = marka yeşili.
// Tarihler yerel gece yarısı `Date` (`lib/calendar-date.ts`); Cube metinleri `new Date(...)` ile okunmaz (AGENTS.md §5).
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type Transition,
  type Variants,
} from 'motion/react'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { FilterField } from '@/components/filters/FilterBar'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  addDays,
  addMonths,
  dateInputToLocalDate,
  dateKey,
  dayDiff,
  localDateToDateInput,
  monthDiff,
  monthStart,
  sameDay,
  shiftMonths,
  startOfDay,
} from '@/lib/calendar-date'
import {
  DATE_PRESETS,
  matchPreset,
  presetRange,
  resolveDefaultRange,
  type DatePreset,
} from '@/lib/date-range'
import { formatDateOnly, RELATIVE_RANGE_PATTERN } from '@/lib/format'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { useToday } from '@/lib/use-today'
import { cn } from '@/lib/utils'

const t = messages.filters
const LOCALE = 'tr-TR'

/** Gün başı / gün sonu: saat yazılmamışsa geçerli olan, adreste tutulmayan değerler. */
const DAY_START = '00:00'
const DAY_END = '23:59'

const pad = (value: number) => String(value).padStart(2, '0')
const HOURS = Array.from({ length: 24 }, (_, hour) => pad(hour))
const MINUTES = Array.from({ length: 60 }, (_, minute) => pad(minute))
const toItems = (values: string[]) => values.map((value) => ({ value, label: value }))

export interface DateTimeRange {
  from: string | undefined
  to: string | undefined
  /** `SS:DD`; `undefined` = gün başı (00:00) / gün sonu (23:59). Yalnız `withTime`'da. */
  timeFrom?: string
  timeTo?: string
}

interface DateRangePickerProps {
  from: string | undefined
  to: string | undefined
  onChange: (range: DateTimeRange) => void
  idPrefix: string
  /**
   * Raporun varsayılan aralığı (`thisMonth`, `-1y`). Hazır aralıklardan birine denk
   * gelmiyorsa listede ayrı seçenek olur.
   */
  defaultRange?: string
  /** Takvimin altında başlangıç/bitiş saati. Hazır aralık seçilince saatler sıfırlanır. */
  withTime?: boolean
  timeFrom?: string
  timeTo?: string
}

/** İki ucu dahil, tam günlerden oluşan aralık. */
interface Range {
  start: Date
  end: Date
}

type Size = { w: number; h: number }
type Bezier = [number, number, number, number]

const ordered = (a: Date, b: Date): Range =>
  dayDiff(a, b) <= 0 ? { start: a, end: b } : { start: b, end: a }
const clampDate = (date: Date, min?: Date, max?: Date) =>
  min && dayDiff(date, min) < 0
    ? startOfDay(min)
    : max && dayDiff(date, max) > 0
      ? startOfDay(max)
      : date

const { blur } = motionTokens
const enterEase = [...motionTokens.ease.enter] as Bezier
const standardEase = [...motionTokens.ease.standard] as Bezier
/** Süreli yaylar katılık/sönüm olarak yeniden yazılır; her hedef değişiminde uçuştaki hız korunur. */
const physical = (visualDuration: number, bounce: number): Transition => {
  const root = (2 * Math.PI) / (visualDuration * 1.2)
  return { type: 'spring', stiffness: root * root, damping: 2 * (1 - bounce) * root, mass: 1 }
}
const GROW = physical(0.48, 0)
const SHRINK = physical(0.38, 0)
const STRETCH = physical(0.3, 0)
const GLIDE = physical(0.3, 0)
const SLIDE = physical(0.4, 0)
const TRIGGER_RADIUS = 18
const PANEL_RADIUS = 26
const WIDE_CELL = 36
const WIDE_MIN = 712
const EDGE = 8

const roll: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    y: `${direction * 0.45}em`,
    filter: `blur(${blur.subtle}px)`,
  }),
  center: {
    opacity: 1,
    y: '0em',
    filter: 'blur(0px)',
    transition: { duration: 0.26, ease: enterEase },
  },
  exit: (direction: number) => ({
    opacity: 0,
    y: `${direction * -0.45}em`,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: 0.14, ease: standardEase },
  }),
}
const fade: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, y: '0em', filter: 'blur(0px)', transition: { duration: 0.12 } },
  exit: { opacity: 0, transition: { duration: 0.08 } },
}
/** Panel içeriği büyüyen yüzeyin hemen arkasından gelir, ondan önce çıkar. */
const faceIn: Variants = {
  hidden: { opacity: 0, y: -4, filter: `blur(${blur.soft}px)` },
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.26, ease: enterEase, delay: 0.07 },
  },
  gone: {
    opacity: 0,
    y: -2,
    filter: `blur(${blur.soft}px)`,
    transition: { duration: 0.12, ease: standardEase },
  },
}
const faceFade: Variants = {
  hidden: { opacity: 0 },
  shown: { opacity: 1, transition: { duration: 0.12 } },
  gone: { opacity: 0, transition: { duration: 0.08 } },
}
const slide: Variants = {
  enter: (direction: number) => ({ opacity: 0, x: direction * 48 }),
  center: {
    opacity: 1,
    x: 0,
    transition: { x: SLIDE, opacity: { duration: 0.22, ease: enterEase } },
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -36,
    transition: { x: SLIDE, opacity: { duration: 0.14, ease: standardEase } },
  }),
}

/** Sözcükler tek tek döner: ileri bir tarih aşağıdan yükselir, geri olan yukarıdan iner; değişmeyen sözcük durur. */
function Rolling({
  text,
  direction,
  reduced,
}: {
  text: string
  direction: number
  reduced: boolean
}) {
  const words = text.split(/\s+/).filter(Boolean)
  return (
    <span className="inline-flex gap-[0.28em] whitespace-nowrap" aria-hidden="true">
      {words.map((word, index) => (
        <span key={index} className="relative inline-block">
          <AnimatePresence initial={false} mode="popLayout" custom={direction}>
            <motion.span
              key={word}
              className="inline-block"
              custom={direction}
              variants={reduced ? fade : roll}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {word}
            </motion.span>
          </AnimatePresence>
        </span>
      ))}
    </span>
  )
}

/** Aralığın hangi yöne gittiğini hatırlar; etiketler zamanın yönünde döner. */
function useDirection(range: Range | null) {
  const time = range ? range.start.getTime() * 2 + range.end.getTime() : null
  const [previous, setPrevious] = useState(time)
  const [direction, setDirection] = useState(1)
  if (time !== previous) {
    setPrevious(time)
    setDirection(time === null || previous === null || time >= previous ? 1 : -1)
  }
  return direction
}

type Segment = { left: number; width: number } | null
const cellIn = (date: Date, first: Date) => {
  const index = dayDiff(date, first)
  return { row: Math.floor(index / 7), col: index % 7 }
}

/** Vurgunun bir haftalık dilimi. Aralığın geldiği kenardan uzar; üzerine gelmek germe gibi okunur. */
function Bar({ row, segment, reduced }: { row: number; segment: Segment; reduced: boolean }) {
  const left = useMotionValue(segment?.left ?? 0)
  const width = useMotionValue(segment?.width ?? 0)
  const opacity = useMotionValue(segment ? 1 : 0)
  const leftPct = useTransform(left, (value) => `${value}%`)
  const widthPct = useTransform(width, (value) => `${value}%`)
  const wasEmpty = useRef(!segment)
  useLayoutEffect(() => {
    if (!segment) {
      wasEmpty.current = true
      animate(opacity, 0, { duration: reduced ? 0 : 0.14 })
      return
    }
    if (wasEmpty.current || reduced) {
      left.jump(segment.left)
      width.jump(segment.width)
    } else {
      animate(left, segment.left, STRETCH)
      animate(width, segment.width, STRETCH)
    }
    wasEmpty.current = false
    animate(opacity, 1, { duration: reduced ? 0 : 0.16 })
  }, [left, opacity, reduced, segment, width])
  return (
    <motion.span
      className="bg-brand-subtle rounded-pill absolute h-[calc(var(--cell)-6px)]"
      style={{ left: leftPct, width: widthPct, opacity, top: `calc(${row} * var(--cell) + 3px)` }}
    />
  )
}

/** Aralığın dolu ucu. Günden güne süzülür, yalnız uç bu aydan çıkarken solar. */
function Thumb({ cell, reduced }: { cell: { row: number; col: number } | null; reduced: boolean }) {
  const x = useMotionValue(cell?.col ?? 0)
  const y = useMotionValue(cell?.row ?? 0)
  const opacity = useMotionValue(cell ? 1 : 0)
  const scale = useMotionValue(cell ? 1 : 0.8)
  const transform = useTransform(
    () => `translate(${x.get() * 100}%, ${y.get() * 100}%) scale(${scale.get()})`,
  )
  const hidden = useRef(!cell)
  useLayoutEffect(() => {
    if (!cell) {
      hidden.current = true
      animate(opacity, 0, { duration: reduced ? 0 : 0.12 })
      animate(scale, 0.8, { duration: reduced ? 0 : 0.12 })
      return
    }
    if (hidden.current || reduced) {
      x.jump(cell.col)
      y.jump(cell.row)
    } else {
      animate(x, cell.col, GLIDE)
      animate(y, cell.row, GLIDE)
    }
    hidden.current = false
    animate(opacity, 1, { duration: reduced ? 0 : 0.16 })
    animate(scale, 1, reduced ? { duration: 0 } : GLIDE)
  }, [cell, opacity, reduced, scale, x, y])
  return (
    <motion.span
      className="before:bg-control-on absolute top-0 left-0 size-(--cell) before:absolute before:inset-[3px] before:rounded-full before:content-['']"
      style={{ transform, opacity }}
    />
  )
}

interface Formatters {
  title: Intl.DateTimeFormat
  day: Intl.DateTimeFormat
  weekday: Intl.DateTimeFormat
  weekdayLong: Intl.DateTimeFormat
}

interface MonthProps {
  month: Date
  range: Range | null
  tabbable: string
  today?: Date
  weekStartsOn: 0 | 1
  reduced: boolean
  formatters: Formatters
  onPick: (date: Date) => void
  onHover: (date: Date) => void
  onKey: (event: ReactKeyboardEvent<HTMLButtonElement>, date: Date) => void
  onFocusDay: (date: Date) => void
  idBase: string
}

function Month({
  month,
  range,
  tabbable,
  today,
  weekStartsOn,
  reduced,
  formatters,
  onPick,
  onHover,
  onKey,
  onFocusDay,
  idBase,
}: MonthProps) {
  const first = useMemo(
    () => addDays(month, -((month.getDay() - weekStartsOn + 7) % 7)),
    [month, weekStartsOn],
  )
  const rows = useMemo(
    () =>
      Array.from({ length: 6 }, (_, row) =>
        Array.from({ length: 7 }, (_, col) => addDays(first, row * 7 + col)),
      ),
    [first],
  )
  const titleId = `${idBase}-${dateKey(month)}`
  const inMonth = (date: Date) => monthDiff(date, month) === 0

  const lo = range?.start
  const hi = range?.end
  const loKey = lo ? dateKey(lo) : ''
  const hiKey = hi ? dateKey(hi) : ''
  /** Her haftanın aralıktan payı, satırın yüzdesi olarak. Aralığın günleriyle anahtarlanır; çubuklar yalnız aralık değişince oynar. */
  const segments = useMemo<Segment[]>(() => {
    if (!loKey || !hiKey) return rows.map(() => null)
    const start = dateInputToLocalDate(loKey)!
    const end = dateInputToLocalDate(hiKey)!
    const unit = 100 / 7
    const colOf = (date: Date) => dayDiff(date, first) % 7
    return rows.map((row) => {
      const days = row.filter((date) => monthDiff(date, month) === 0)
      if (!days.length) return null
      const rowFirst = days[0]!
      const rowLast = days[days.length - 1]!
      const from = dayDiff(start, rowFirst) > 0 ? start : rowFirst
      const to = dayDiff(end, rowLast) < 0 ? end : rowLast
      if (dayDiff(from, to) <= 0) {
        const a = colOf(from)
        const b = colOf(to)
        return { left: a * unit, width: (b - a + 1) * unit }
      }
      // Hafta tümüyle aralığın öncesinde ya da sonrasında: aralığın içeri gireceği kenarda büzül.
      if (dayDiff(rowLast, start) < 0) return { left: (colOf(rowLast) + 1) * unit, width: 0 }
      return { left: colOf(rowFirst) * unit, width: 0 }
    })
  }, [first, hiKey, loKey, month, rows])
  const startCell = useMemo(
    () =>
      loKey && monthDiff(dateInputToLocalDate(loKey)!, month) === 0
        ? cellIn(dateInputToLocalDate(loKey)!, first)
        : null,
    [loKey, month, first],
  )
  const endCell = useMemo(
    () =>
      hiKey && hiKey !== loKey && monthDiff(dateInputToLocalDate(hiKey)!, month) === 0
        ? cellIn(dateInputToLocalDate(hiKey)!, first)
        : null,
    [hiKey, loKey, month, first],
  )

  return (
    <div className="w-[calc(var(--cell)*7)]">
      <p id={titleId} className="m-0 h-8 text-center leading-8 font-medium whitespace-nowrap">
        {formatters.title.format(month)}
      </p>
      <div role="grid" aria-labelledby={titleId} className="grid">
        <div
          role="row"
          className="text-text-muted grid h-7 grid-cols-[repeat(7,var(--cell))] items-center text-center text-xs"
        >
          {rows[0]!.map((date) => (
            <span
              key={date.getDay()}
              role="columnheader"
              aria-label={formatters.weekdayLong.format(date)}
            >
              {formatters.weekday.format(date).slice(0, 2)}
            </span>
          ))}
        </div>
        <div className="relative">
          <span className="pointer-events-none absolute inset-0" aria-hidden="true">
            {segments.map((segment, row) => (
              <Bar key={row} row={row} segment={segment} reduced={reduced} />
            ))}
            <Thumb cell={startCell} reduced={reduced} />
            <Thumb cell={endCell} reduced={reduced} />
          </span>
          {rows.map((row, index) => (
            <div
              key={index}
              role="row"
              className="grid h-(--cell) grid-cols-[repeat(7,var(--cell))]"
            >
              {row.map((date) => {
                if (!inMonth(date))
                  return <span key={dateKey(date)} role="gridcell" className="block" />
                const key = dateKey(date)
                const inRange = Boolean(
                  lo && hi && dayDiff(date, lo) >= 0 && dayDiff(date, hi) <= 0,
                )
                const edge = sameDay(date, lo) || sameDay(date, hi)
                const isToday = sameDay(date, today)
                return (
                  <span
                    key={key}
                    role="gridcell"
                    aria-selected={inRange}
                    className="relative block"
                  >
                    <button
                      type="button"
                      className={cn(
                        'group/day text-foreground relative z-[1] grid size-full cursor-pointer place-items-center rounded-full border-0 bg-transparent p-0 tabular-nums [-webkit-tap-highlight-color:transparent]',
                        'ease-standard transition-colors duration-(--duration-fast)',
                        'data-[edge]:text-control-glyph data-[edge]:font-medium data-[edge]:delay-40',
                        'disabled:text-text-muted disabled:cursor-default disabled:opacity-50',
                        "data-[today]:after:bg-control-on data-[today]:data-[edge]:after:bg-control-glyph data-[today]:after:absolute data-[today]:after:bottom-[5px] data-[today]:after:left-1/2 data-[today]:after:-ml-0.5 data-[today]:after:size-1 data-[today]:after:rounded-full data-[today]:after:content-['']",
                        'motion-reduce:transition-none',
                      )}
                      data-date={key}
                      data-edge={edge || undefined}
                      data-range={inRange || undefined}
                      data-today={isToday || undefined}
                      tabIndex={key === tabbable ? 0 : -1}
                      aria-current={isToday ? 'date' : undefined}
                      aria-label={formatters.day.format(date)}
                      onClick={() => onPick(date)}
                      onPointerEnter={() => onHover(date)}
                      onFocus={() => onFocusDay(date)}
                      onKeyDown={(event) => onKey(event, date)}
                    >
                      <span
                        className={cn(
                          'relative grid size-[calc(100%-6px)] place-items-center rounded-full',
                          '[transition:background-color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-instant)_var(--ease-standard)]',
                          'group-active/day:group-not-disabled/day:scale-90',
                          'group-hover/day:group-not-disabled/day:not-group-data-[edge]/day:bg-[color-mix(in_oklab,var(--foreground)_7%,transparent)]',
                          'group-focus-visible/day:not-group-data-[edge]/day:bg-[color-mix(in_oklab,var(--foreground)_7%,transparent)]',
                          'motion-reduce:transition-none',
                        )}
                      >
                        {date.getDate()}
                      </span>
                    </button>
                  </span>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Görünen aylar birlikte yolculuk eder; ayrılan küme etkisiz kalır, odak ve sorgular yalnız güncel olanı bulur. */
function Months({
  children,
  direction,
  reduced,
}: {
  children: ReactNode
  direction: number
  reduced: boolean
}) {
  const present = useIsPresent()
  return (
    <motion.div
      className="flex gap-6"
      data-current={present || undefined}
      inert={!present || undefined}
      custom={direction}
      variants={reduced ? fade : slide}
      initial="enter"
      animate="center"
      exit="exit"
    >
      {children}
    </motion.div>
  )
}

/** Panel kapanırken (çıkış animasyonu sürerken) etkisizleşir: odak ve okuyucu görünmez panele girmesin. */
function Panel({ ref, ...props }: ComponentProps<typeof motion.div>) {
  const present = useIsPresent()
  return (
    <motion.div
      ref={ref}
      inert={!present || undefined}
      aria-hidden={!present || undefined}
      {...props}
    />
  )
}

/** Saat ve dakika seçimi (DESIGN §5: yerel `<input type="time">` yerine `ui/select`). */
function TimeSelect({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  const [hour = '00', minute = '00'] = value.split(':')
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="text-text-secondary w-24 text-xs">{label}</span>
      <Select
        value={hour}
        items={toItems(HOURS)}
        onValueChange={(next) => onChange(`${String(next)}:${minute}`)}
      >
        <SelectTrigger size="sm" className="tabular w-18" aria-label={`${label} – ${t.hour}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {HOURS.map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <span aria-hidden="true">:</span>
      <Select
        value={minute}
        items={toItems(MINUTES)}
        onValueChange={(next) => onChange(`${hour}:${String(next)}`)}
      >
        <SelectTrigger size="sm" className="tabular w-18" aria-label={`${label} – ${t.minute}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {MINUTES.map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

/** Taşınmış açılır listeler (saat seçimi) panelin DOM'unda değildir; dışarı tıklama/odak sayılmamalı. */
const inPortal = (node: EventTarget | null) =>
  node instanceof Element && Boolean(node.closest('[data-slot^="select-"]'))

interface Preset {
  key: DatePreset | 'default'
  label: string
  range: { from: string | undefined; to: string | undefined }
}

interface Draft {
  range: Range | null
  /** "Tümü": tarih filtresi yok. `range` boşken Uygula bunu uygular. */
  all: boolean
  timeFrom?: string
  timeTo?: string
}

const toRange = (from: string | undefined, to: string | undefined): Range | null => {
  const start = dateInputToLocalDate(from)
  const end = dateInputToLocalDate(to)
  return start && end && dayDiff(start, end) <= 0 ? { start, end } : null
}

const presetButton =
  'text-text-secondary relative flex h-8 flex-none cursor-pointer items-center rounded-[12px] border-0 bg-transparent px-3 text-left whitespace-nowrap [-webkit-tap-highlight-color:transparent] ease-standard transition-[color,background-color] duration-(--duration-fast) aria-pressed:text-foreground aria-pressed:font-medium hover:text-foreground not-aria-pressed:hover:bg-[color-mix(in_oklab,var(--foreground)_4%,transparent)] focus-visible:bg-[color-mix(in_oklab,var(--foreground)_5%,transparent)] focus-visible:text-foreground group-data-[compact]/panel:px-[11px] motion-reduce:transition-none'

const navButton =
  'text-text-secondary absolute top-0 z-[2] grid size-8 cursor-pointer place-items-center rounded-pill border-0 bg-transparent p-0 [-webkit-tap-highlight-color:transparent] [transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-instant)_var(--ease-standard)] not-disabled:hover:bg-[color-mix(in_oklab,var(--foreground)_6.5%,transparent)] not-disabled:hover:text-foreground focus-visible:bg-[color-mix(in_oklab,var(--foreground)_5%,transparent)] focus-visible:text-foreground not-disabled:active:scale-[0.92] disabled:cursor-default disabled:opacity-35 motion-reduce:transition-none'

const footerButton =
  'rounded-control inline-flex h-(--control-height-sm) cursor-pointer items-center justify-center border-0 px-3.5 font-medium whitespace-nowrap [-webkit-tap-highlight-color:transparent] [transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-instant)_var(--ease-standard),opacity_var(--duration-fast)_var(--ease-standard)] motion-reduce:transition-none'

/**
 * Tarih aralığı: tek düğme, açılan yüzeyde solda hazır aralıklar, sağda iki aylık takvim (dar yerde bir ay) ve
 * istenirse saatler. Hazır aralık ve takvim seçimi taslağa yazılır; "Uygula" tek `onChange` çağırır.
 *
 * Seçili hazır aralık `from`/`to`'dan **türetilir** — ayrı bir state tutulmaz, böylece
 * adres çubuğundaki aralık ile düğme etiketi asla ayrışamaz.
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  idPrefix,
  defaultRange,
  withTime = false,
  timeFrom,
  timeTo,
}: DateRangePickerProps) {
  const reduced = useReducedMotion() ?? false
  const today = useToday()
  const uid = useId()
  const weekStartsOn = 1 as const

  const invalid = Boolean(from && to && from > to)
  // Aynı gün, başlangıç saati bitişten sonra: hata saatte, tarihte değil.
  const timeInvalid = Boolean(
    withTime && from && from === to && (timeFrom ?? DAY_START) > (timeTo ?? DAY_END),
  )

  const committed = useMemo(() => toRange(from, to), [from, to])

  // Hazır aralığa denk gelmeyen göreli varsayılan (`-1y`) ayrı seçenek olur.
  const defaultRangeValue = defaultRange ? resolveDefaultRange(defaultRange) : undefined
  const extraDefault =
    defaultRange &&
    defaultRangeValue &&
    RELATIVE_RANGE_PATTERN.test(defaultRange) &&
    !matchPreset(defaultRangeValue.from, defaultRangeValue.to)
      ? defaultRange
      : undefined
  const presets: Preset[] = [
    ...(extraDefault && defaultRangeValue
      ? [
          {
            key: 'default' as const,
            label: t.range.relative(extraDefault),
            range: defaultRangeValue,
          },
        ]
      : []),
    ...DATE_PRESETS.map((value) => ({
      key: value,
      label: t.range[value],
      range: presetRange(value),
    })),
  ]

  const customTime = withTime && (timeFrom !== undefined || timeTo !== undefined)
  const preset = customTime ? undefined : matchPreset(from, to)
  const isExtraDefault =
    !customTime &&
    extraDefault !== undefined &&
    defaultRangeValue?.from === from &&
    defaultRangeValue?.to === to

  const formatters = useMemo(
    () => ({
      label: new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' }),
      title: new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' }),
      day: new Intl.DateTimeFormat(LOCALE, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
      weekday: new Intl.DateTimeFormat(LOCALE, { weekday: 'short' }),
      weekdayLong: new Intl.DateTimeFormat(LOCALE, { weekday: 'long' }),
    }),
    [],
  )
  const format = useCallback(
    (range: Range) =>
      sameDay(range.start, range.end)
        ? formatters.label.format(range.start)
        : formatters.label.formatRange(range.start, range.end),
    [formatters],
  )
  const withTimes = (day: string | undefined, time: string | undefined, fallback: string) =>
    day ? `${formatDateOnly(day)}${withTime ? ` ${time ?? fallback}` : ''}` : '…'

  // Tetikleyici: hazır aralığın adı (varsa) ve tarihler; tarihler panel altbilgisine uçan metindir.
  const rangeText = committed ? format(committed) : undefined
  const timeSuffix = customTime ? ` · ${timeFrom ?? DAY_START}–${timeTo ?? DAY_END}` : ''
  const name = preset
    ? t.range[preset]
    : isExtraDefault
      ? t.range.relative(extraDefault)
      : undefined
  const fallbackText = `${withTimes(from, timeFrom, DAY_START)} – ${withTimes(to, timeTo, DAY_END)}`
  const triggerName = name ?? (committed ? undefined : fallbackText)
  const triggerDates = committed && rangeText ? `${rangeText}${timeSuffix}` : undefined
  const triggerLabel = [triggerName, triggerDates].filter(Boolean).join(' ')

  const [open, setOpen] = useState(false)
  const [compact, setCompact] = useState(false)
  const [panelWidth, setPanelWidth] = useState(0)
  const [view, setView] = useState<Date>(() => monthStart(committed?.end ?? new Date()))
  const [direction, setDirection] = useState(1)
  const [draft, setDraft] = useState<Draft>({ range: committed, all: !from && !to })
  const [anchor, setAnchor] = useState<Date | null>(null)
  const [hover, setHover] = useState<Date | null>(null)
  const [focusKey, setFocusKey] = useState('')

  const count = compact ? 1 : 2
  const visible = useMemo(
    () => Array.from({ length: count }, (_, index) => addMonths(view, index)),
    [count, view],
  )
  const shown = anchor ? ordered(anchor, hover ?? anchor) : draft.range
  const draftPreset = (() => {
    if (anchor) return -1
    if (draft.all) return presets.findIndex((item) => item.key === 'all')
    if (!draft.range) return -1
    const start = localDateToDateInput(draft.range.start)
    const end = localDateToDateInput(draft.range.end)
    return presets.findIndex((item) => item.range.from === start && item.range.to === end)
  })()
  const activePreset = draft.timeFrom !== undefined || draft.timeTo !== undefined ? -1 : draftPreset
  const days = shown ? dayDiff(shown.end, shown.start) + 1 : 0
  const committedDirection = useDirection(committed)
  const shownDirection = useDirection(shown)
  const draftTimeInvalid = Boolean(
    withTime &&
    shown &&
    sameDay(shown.start, shown.end) &&
    (draft.timeFrom ?? DAY_START) > (draft.timeTo ?? DAY_END),
  )

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const pendingFocus = useRef<'trigger' | 'day' | null>(null)

  /* ---------- Tek yüzey: tetikleyicinin kutusu panelin kutusuna, panel de geri döner ---------- */
  // İlk ölçümden önce yüzey köke dolar, kök tetikleyiciyi sarar; sunucu çıktısı da doğru görünür.
  const width = useMotionValue<number | string>('100%')
  const height = useMotionValue<number | string>('100%')
  const radius = useMotionValue(TRIGGER_RADIUS)
  const x = useMotionValue(0)
  const rootWidth = useMotionValue<number | string>('auto')
  const sizes = useRef<{ trigger: Size; panel: Size | null }>({
    trigger: { w: 0, h: 0 },
    panel: null,
  })
  const live = useRef({ open: false, reduced: false, ready: false })
  const queued = useRef(false)

  const bounds = useCallback(() => ({ left: 0, right: document.documentElement.clientWidth }), [])

  const update = useCallback(() => {
    queued.current = false
    const { trigger, panel } = sizes.current
    const state = live.current
    if (!trigger.w) return
    const openPanel = state.open && panel ? panel : null
    const target = openPanel ?? trigger
    let offset = 0
    if (openPanel && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect()
      const box = bounds()
      offset = Math.min(0, box.right - EDGE - (rect.left + openPanel.w))
      offset = Math.max(offset, box.left + EDGE - rect.left)
    }
    const r = openPanel ? PANEL_RADIUS : TRIGGER_RADIUS
    if (!state.ready || state.reduced) {
      width.jump(target.w)
      height.jump(target.h)
      radius.jump(r)
      x.jump(offset)
      rootWidth.jump(trigger.w)
      state.ready = true
      return
    }
    const spring = openPanel ? GROW : state.open ? GROW : SHRINK
    animate(width, target.w, spring)
    animate(height, target.h, spring)
    animate(radius, r, spring)
    animate(x, offset, spring)
    // Kök (yerleşimdeki yer) tetikleyiciyle aynı karede değişir; yayla süzülürse komşu filtreler kayar.
    rootWidth.jump(trigger.w)
  }, [bounds, height, radius, rootWidth, width, x])

  const schedule = useCallback(() => {
    if (queued.current) return
    queued.current = true
    queueMicrotask(update)
  }, [update])

  useLayoutEffect(() => {
    live.current.open = open
    live.current.reduced = reduced
    if (!open) sizes.current.panel = null
    schedule()
  }, [open, reduced, schedule])

  useLayoutEffect(() => {
    const node = triggerRef.current
    if (!node) return
    const read = () => {
      sizes.current.trigger = { w: node.offsetWidth, h: node.offsetHeight }
      schedule()
    }
    read()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(read)
    observer.observe(node)
    return () => observer.disconnect()
  }, [schedule])

  useLayoutEffect(() => {
    const node = panelRef.current
    if (!open || !node) return
    const read = () => {
      sizes.current.panel = { w: node.offsetWidth, h: node.offsetHeight }
      schedule()
    }
    read()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(read)
    observer.observe(node)
    return () => observer.disconnect()
  }, [open, schedule])

  // Kısa ekranda (telefon, sayfanın altındaki tetikleyici) panel kıvrımın altında açılıp Uygula erişilmez olabilir.
  // Boyutunu alınca altı görünene kadar kaydırılır, üstünü geçmez. Kapanınca kaydırma tetikleyiciyi dışarıda
  // bıraktıysa tetikleyici görünüme getirilir.
  useEffect(() => {
    if (!open) return
    const frame = requestAnimationFrame(() => {
      const node = panelRef.current
      if (!node) return
      const rect = node.getBoundingClientRect()
      const overflow = rect.bottom - (window.innerHeight - EDGE)
      if (overflow <= 0) return
      window.scrollBy({
        top: Math.min(overflow, Math.max(0, rect.top - EDGE)),
        behavior: reduced ? 'instant' : 'smooth',
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [open, reduced])
  useEffect(() => {
    if (open) return
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    if (rect.bottom < 0 || rect.top > window.innerHeight)
      trigger.scrollIntoView({ block: 'center', behavior: reduced ? 'instant' : 'smooth' })
  }, [open, reduced])

  /* ---------- Yerleşim: yer varsa iki ay, yoksa tek kompakt ay ---------- */
  const measureLayout = useCallback(() => {
    const box = bounds()
    const available = Math.max(0, box.right - box.left - EDGE * 2)
    return { single: available < WIDE_MIN, width: Math.min(352, available) }
  }, [bounds])

  useEffect(() => {
    if (!open) return
    const onResize = () => {
      const next = measureLayout()
      setCompact(next.single)
      setPanelWidth(next.width)
      schedule()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [measureLayout, open, schedule])

  const viewFor = (range: Range | null, single: boolean, fallback: Date) => {
    const end = monthStart(range?.end ?? fallback)
    return single ? end : addMonths(end, -1)
  }

  const openPanel = () => {
    if (!today) return
    const layout = measureLayout()
    setCompact(layout.single)
    setPanelWidth(layout.width)
    setDraft({ range: committed, all: !from && !to, timeFrom, timeTo })
    setAnchor(null)
    setHover(null)
    setView(viewFor(committed, layout.single, today))
    setDirection(0)
    setFocusKey(dateKey(committed?.start ?? today))
    pendingFocus.current = 'day'
    setOpen(true)
  }

  const close = useCallback((focus: boolean) => {
    if (focus) pendingFocus.current = 'trigger'
    setDirection(0)
    setOpen(false)
    setAnchor(null)
    setHover(null)
  }, [])

  const canApply = Boolean(shown) || draft.all
  const apply = () => {
    if (!canApply || draftTimeInvalid) return
    const next = anchor ? { start: anchor, end: anchor } : draft.range
    onChange({
      from: next ? localDateToDateInput(next.start) : undefined,
      to: next ? localDateToDateInput(next.end) : undefined,
      timeFrom: withTime ? draft.timeFrom : undefined,
      timeTo: withTime ? draft.timeTo : undefined,
    })
    close(true)
  }

  /* ---------- Odak yönetimi ---------- */
  useLayoutEffect(() => {
    const target = pendingFocus.current
    if (!target) return
    if (target === 'trigger' && !open) {
      pendingFocus.current = null
      triggerRef.current?.focus({ preventScroll: true })
      return
    }
    if (target === 'day' && open) {
      const node = panelRef.current?.querySelector<HTMLButtonElement>(
        `[data-current] [data-date="${focusKey}"]`,
      )
      if (node) {
        pendingFocus.current = null
        node.focus({ preventScroll: true })
      }
    }
  }, [focusKey, open, view, compact])

  useEffect(() => {
    if (!open) return
    const down = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node) && !inPortal(event.target)) close(false)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [close, open])

  const onRootKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open && !inPortal(event.target)) {
      event.preventDefault()
      event.stopPropagation()
      close(true)
    }
  }
  const onRootBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null
    if (open && next && !event.currentTarget.contains(next) && !inPortal(next)) close(false)
  }

  /* ---------- Ay gezintisi ve seçim ---------- */
  const goTo = (next: Date) => {
    const delta = monthDiff(next, view)
    if (!delta) return
    setDirection(Math.sign(delta))
    setView(next)
  }

  /** Aralığı ekranda tutar: sığıyorsa dokunmaz, yoksa son ayı sağa yaslar. */
  const reveal = (range: Range) => {
    const firstMonth = view
    const lastMonth = addMonths(view, count - 1)
    if (monthDiff(range.start, firstMonth) >= 0 && monthDiff(range.end, lastMonth) <= 0) return
    goTo(viewFor(range, compact, range.end))
  }

  const pick = (date: Date) => {
    setFocusKey(dateKey(date))
    if (!anchor) {
      setAnchor(date)
      setHover(date)
      return
    }
    setDraft((current) => ({ ...current, range: ordered(anchor, date), all: false }))
    setAnchor(null)
    setHover(null)
  }

  const choosePreset = (item: Preset) => {
    const range = toRange(item.range.from, item.range.to)
    // Hazır aralık saatleri sıfırlar.
    setDraft({ range, all: !range, timeFrom: undefined, timeTo: undefined })
    setAnchor(null)
    setHover(null)
    if (range) {
      setFocusKey(dateKey(range.start))
      reveal(range)
    }
  }

  const onDayKey = (event: ReactKeyboardEvent<HTMLButtonElement>, date: Date) => {
    const weekday = (date.getDay() - weekStartsOn + 7) % 7
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(date, -1),
      ArrowRight: () => addDays(date, 1),
      ArrowUp: () => addDays(date, -7),
      ArrowDown: () => addDays(date, 7),
      Home: () => addDays(date, -weekday),
      End: () => addDays(date, 6 - weekday),
      PageUp: () => shiftMonths(date, event.shiftKey ? -12 : -1),
      PageDown: () => shiftMonths(date, event.shiftKey ? 12 : 1),
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    const next = clampDate(move())
    setFocusKey(dateKey(next))
    if (anchor) setHover(next)
    pendingFocus.current = 'day'
    const firstMonth = view
    const lastMonth = addMonths(view, count - 1)
    if (monthDiff(next, firstMonth) < 0) goTo(monthStart(next))
    else if (monthDiff(next, lastMonth) > 0) goTo(addMonths(monthStart(next), -(count - 1)))
  }

  const onRailKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const keys = compact ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown']
    if (!keys.includes(event.key)) return
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-preset]'),
    )
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (at < 0) return
    event.preventDefault()
    buttons[(at + (event.key === keys[1] ? 1 : -1) + buttons.length) % buttons.length]?.focus()
  }

  /** Sekme durağı: odaktaki gün ekrandaysa o, yoksa aralığın ilk görünen günü, yoksa ayın 1'i. */
  const tabbable = useMemo(() => {
    const onScreen = (date: Date) => visible.some((month) => monthDiff(date, month) === 0)
    const focused = dateInputToLocalDate(focusKey)
    if (focused && onScreen(focused)) return focusKey
    if (shown && onScreen(shown.start)) return dateKey(shown.start)
    return dateKey(visible[0]!)
  }, [focusKey, shown, visible])

  /* ---------- Hazır aralık vurgusu presetler arasında süzülür ---------- */
  const gx = useMotionValue(0)
  const gy = useMotionValue(0)
  const gw = useMotionValue(0)
  const gh = useMotionValue(0)
  const go = useMotionValue(0)
  useLayoutEffect(() => {
    const rail = railRef.current
    const node =
      activePreset < 0 ? null : rail?.querySelector<HTMLElement>(`[data-preset="${activePreset}"]`)
    if (!rail || !node) {
      animate(go, 0, { duration: reduced ? 0 : 0.14 })
      return
    }
    const place = [node.offsetLeft, node.offsetTop, node.offsetWidth, node.offsetHeight] as const
    if (go.get() < 0.05 || reduced) {
      gx.jump(place[0])
      gy.jump(place[1])
      gw.jump(place[2])
      gh.jump(place[3])
    } else {
      animate(gx, place[0], GLIDE)
      animate(gy, place[1], GLIDE)
      animate(gw, place[2], GLIDE)
      animate(gh, place[3], GLIDE)
    }
    animate(go, 1, { duration: reduced ? 0 : 0.16 })
    if (
      compact &&
      (node.offsetLeft < rail.scrollLeft ||
        node.offsetLeft + node.offsetWidth > rail.scrollLeft + rail.clientWidth)
    ) {
      rail.scrollTo({ left: node.offsetLeft - 12, behavior: reduced ? 'auto' : 'smooth' })
    }
  }, [activePreset, compact, go, gh, gw, gx, gy, open, reduced])

  /* ---------- Metinler ---------- */
  const shownText = shown ? format(shown) : t.rangeNone
  const countText = shown ? t.rangeDays(days) : ''
  const status = !open
    ? ''
    : anchor
      ? t.rangeStarted(formatters.label.format(anchor))
      : shown
        ? `${shownText}, ${countText}`
        : ''
  const cellSize =
    compact && panelWidth
      ? Math.max(32, Math.min(42, Math.floor((panelWidth - 24) / 7)))
      : WIDE_CELL

  const quiet = open
    ? reduced
      ? { opacity: 0 }
      : { opacity: 0, filter: `blur(${blur.subtle}px)` }
    : { opacity: 1, filter: 'blur(0px)' }
  const quietTransition = open
    ? { duration: 0.12, ease: standardEase }
    : { duration: 0.22, ease: enterEase, delay: reduced ? 0 : 0.1 }

  return (
    // Alan en uzun hazır aralık etiketine yetecek yer ayırır: Uygula'da etiket kısalıp uzayınca komşu filtreler
    // ve çubuğun satır kırılımı yerinde kalır.
    <div className="flex flex-wrap items-end gap-2 sm:min-w-84">
      <FilterField label={t.dateRange} htmlFor={`${idPrefix}-range`}>
        <motion.div
          ref={rootRef}
          className={cn(
            'group/drp text-foreground leading-body relative z-0 inline-block h-(--control-height-md) align-top text-sm tracking-(--tracking-body)',
            '[transition:z-index_0s_linear_.5s] data-[open]:z-30 data-[open]:[transition-delay:0s]',
          )}
          style={{ width: rootWidth }}
          data-open={open || undefined}
          data-invalid={invalid || timeInvalid || undefined}
          onKeyDown={onRootKey}
          onBlur={onRootBlur}
        >
          {/* Tetikleyici panelin altında yerinde durur; açıkken simgeleri ve değeri solar. */}
          <button
            ref={triggerRef}
            id={`${idPrefix}-range`}
            data-trigger=""
            type="button"
            className={cn(
              'text-foreground relative z-[1] inline-flex h-(--control-height-md) w-max cursor-pointer items-center gap-2.5 border-0 bg-transparent pr-3 pl-3.5 font-medium whitespace-nowrap [-webkit-tap-highlight-color:transparent] disabled:cursor-default [&[inert]]:pointer-events-none',
            )}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? `${uid}-panel` : undefined}
            aria-invalid={invalid || timeInvalid || undefined}
            aria-label={`${t.dateRange}: ${triggerLabel}`}
            disabled={!today}
            inert={open || undefined}
            onClick={openPanel}
          >
            <motion.span
              className="text-text-secondary grid flex-none place-items-center"
              initial={false}
              animate={quiet}
              transition={quietTransition}
            >
              <CalendarDays className="size-4" strokeWidth={1.75} aria-hidden="true" />
            </motion.span>
            {triggerName && (
              <motion.span
                // Mobilde ad + tarih filtre kartına sığmıyor; tarih kalır, ad aria-label'da.
                className={cn('inline-flex tabular-nums', triggerDates && 'max-sm:hidden')}
                initial={false}
                animate={open ? { opacity: 0 } : { opacity: 1 }}
                transition={quietTransition}
              >
                <Rolling text={triggerName} direction={committedDirection} reduced={reduced} />
              </motion.span>
            )}
            {triggerDates && (
              <motion.span
                className={cn(
                  'inline-flex tabular-nums',
                  triggerName && 'text-text-secondary font-normal',
                )}
                initial={false}
                animate={open ? { opacity: 0 } : { opacity: 1 }}
                transition={quietTransition}
              >
                <Rolling text={triggerDates} direction={committedDirection} reduced={reduced} />
              </motion.span>
            )}
            <motion.span
              className="text-text-muted grid flex-none place-items-center"
              initial={false}
              animate={quiet}
              transition={quietTransition}
            >
              <ChevronDown className="size-4" strokeWidth={1.75} aria-hidden="true" />
            </motion.span>
          </button>

          <motion.div
            className={cn(
              'bg-surface-raised shadow-resting absolute top-0 left-0 z-[2] overflow-clip',
              '[transition:box-shadow_var(--duration-considered)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard)]',
              'after:border-border after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:transition-colors after:duration-(--duration-fast) after:content-[""]',
              'group-data-[open]/drp:shadow-floating group-not-data-[open]/drp:z-0',
              'group-not-data-[open]/drp:group-has-[button[data-trigger]:hover:not(:disabled)]/drp:bg-[color-mix(in_oklab,var(--foreground)_3%,var(--surface-raised))]',
              'group-not-data-[open]/drp:group-has-[button[data-trigger]:hover:not(:disabled)]/drp:after:border-border-strong',
              'group-data-[invalid]/drp:after:border-danger',
              'motion-reduce:transition-none',
            )}
            style={{ width, height, borderRadius: radius, x }}
          >
            <AnimatePresence>
              {open && today && (
                <Panel
                  key="panel"
                  ref={panelRef}
                  id={`${uid}-panel`}
                  className="group/panel absolute top-0 left-0 w-max p-2.5 data-[compact]:p-2"
                  role="dialog"
                  aria-label={t.dateRange}
                  data-compact={compact || undefined}
                  style={
                    {
                      '--cell': `${cellSize}px`,
                      width: compact && panelWidth ? panelWidth : undefined,
                    } as CSSProperties
                  }
                  exit={{ opacity: 1, transition: { duration: 0.14 } }}
                >
                  <motion.div
                    className="flex gap-2.5 group-data-[compact]/panel:flex-col group-data-[compact]/panel:gap-2"
                    variants={reduced ? faceFade : faceIn}
                    initial="hidden"
                    animate="shown"
                    exit="gone"
                  >
                    <div
                      ref={railRef}
                      className={cn(
                        'border-border-subtle relative flex w-37 flex-none flex-col gap-0.5 border-r pr-2.5',
                        'group-data-[compact]/panel:-mx-2 group-data-[compact]/panel:w-auto group-data-[compact]/panel:[scrollbar-width:none] group-data-[compact]/panel:flex-row group-data-[compact]/panel:overflow-x-auto group-data-[compact]/panel:overscroll-x-contain group-data-[compact]/panel:border-r-0 group-data-[compact]/panel:border-b group-data-[compact]/panel:px-2 group-data-[compact]/panel:pr-2 group-data-[compact]/panel:pb-2 group-data-[compact]/panel:[&::-webkit-scrollbar]:hidden',
                      )}
                      role="group"
                      aria-label={t.presetsRegion}
                      onKeyDown={onRailKey}
                    >
                      <motion.span
                        className="pointer-events-none absolute top-0 left-0 rounded-[12px] bg-[color-mix(in_oklab,var(--foreground)_6.5%,transparent)]"
                        style={{ x: gx, y: gy, width: gw, height: gh, opacity: go }}
                        aria-hidden="true"
                      />
                      {presets.map((item, index) => (
                        <button
                          key={item.key}
                          type="button"
                          className={presetButton}
                          data-preset={index}
                          aria-pressed={index === activePreset}
                          onClick={() => choosePreset(item)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <div
                      className="relative px-0.5 group-data-[compact]/panel:self-center"
                      onPointerLeave={() => {
                        if (anchor) setHover(null)
                      }}
                    >
                      <button
                        type="button"
                        className={cn(navButton, 'left-0.5')}
                        aria-label={messages.calendar.previousMonth}
                        onClick={() => goTo(addMonths(view, -1))}
                      >
                        <ChevronLeft className="size-4" strokeWidth={1.75} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className={cn(navButton, 'right-0.5')}
                        aria-label={messages.calendar.nextMonth}
                        onClick={() => goTo(addMonths(view, 1))}
                      >
                        <ChevronRight className="size-4" strokeWidth={1.75} aria-hidden="true" />
                      </button>
                      {/* Görünüm altı hafta sabit yüksekliktedir; giden ve gelen aylar tek hücrede üst üste durur, panel kayarken boyut değiştirmez. */}
                      <div className="relative grid h-[calc(60px+var(--cell)*6)] overflow-clip [overflow-clip-margin:2px] *:col-start-1 *:row-start-1">
                        <AnimatePresence initial={false} custom={direction}>
                          <Months
                            key={`${dateKey(view)}-${count}`}
                            direction={direction}
                            reduced={reduced}
                          >
                            {visible.map((month) => (
                              <Month
                                key={dateKey(month)}
                                month={month}
                                range={shown}
                                tabbable={tabbable}
                                today={today}
                                weekStartsOn={weekStartsOn}
                                reduced={reduced}
                                formatters={formatters}
                                idBase={uid}
                                onPick={pick}
                                onHover={(date) => {
                                  if (anchor) setHover(date)
                                }}
                                onKey={onDayKey}
                                onFocusDay={(date) => setFocusKey(dateKey(date))}
                              />
                            ))}
                          </Months>
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>

                  {withTime && (
                    <motion.div
                      className="border-border-subtle mt-2.5 flex flex-col gap-1.5 border-t px-3 pt-2.5"
                      variants={reduced ? faceFade : faceIn}
                      initial="hidden"
                      animate="shown"
                      exit="gone"
                    >
                      <TimeSelect
                        label={t.timeFrom}
                        value={draft.timeFrom ?? DAY_START}
                        onChange={(time) =>
                          setDraft((current) => ({
                            ...current,
                            timeFrom: time === DAY_START ? undefined : time,
                          }))
                        }
                      />
                      <TimeSelect
                        label={t.timeTo}
                        value={draft.timeTo ?? DAY_END}
                        onChange={(time) =>
                          setDraft((current) => ({
                            ...current,
                            timeTo: time === DAY_END ? undefined : time,
                          }))
                        }
                      />
                      {draftTimeInvalid && (
                        <p role="alert" className="text-danger text-xs">
                          {t.timeRangeInvalid}
                        </p>
                      )}
                    </motion.div>
                  )}

                  <div className="border-border-subtle mt-2.5 flex w-0 min-w-full items-center justify-between gap-3 border-t pt-2.5 pr-0.5 pl-3 group-data-[compact]/panel:mt-1.5 group-data-[compact]/panel:flex-wrap group-data-[compact]/panel:pl-[11px]">
                    <div className="grid min-w-0 gap-px group-data-[compact]/panel:flex-[1_1_8rem]">
                      <motion.span
                        className="inline-flex w-max font-medium tabular-nums"
                        variants={reduced ? faceFade : faceIn}
                        initial="hidden"
                        animate="shown"
                        exit="gone"
                      >
                        <Rolling text={shownText} direction={shownDirection} reduced={reduced} />
                      </motion.span>
                      <motion.span
                        className="text-text-muted inline-flex min-h-[1.4em] text-xs tabular-nums"
                        variants={reduced ? faceFade : faceIn}
                        initial="hidden"
                        animate="shown"
                        exit="gone"
                      >
                        {countText && (
                          <Rolling
                            text={anchor && sameDay(anchor, hover) ? t.pickEndDate : countText}
                            direction={shownDirection}
                            reduced={reduced}
                          />
                        )}
                      </motion.span>
                    </div>
                    <motion.div
                      className="flex flex-none items-center gap-1.5 group-data-[compact]/panel:ml-auto"
                      variants={reduced ? faceFade : faceIn}
                      initial="hidden"
                      animate="shown"
                      exit="gone"
                    >
                      <button
                        type="button"
                        className={cn(
                          footerButton,
                          'text-text-secondary focus-visible:bg-surface-muted focus-visible:text-foreground hover:bg-surface-muted hover:text-foreground bg-transparent active:scale-[0.97]',
                        )}
                        onClick={() => close(true)}
                      >
                        {messages.common.cancel}
                      </button>
                      <button
                        type="button"
                        className={cn(
                          footerButton,
                          'bg-control-on text-control-glyph min-w-19 not-disabled:hover:bg-[color-mix(in_oklab,var(--control-on)_86%,black)] not-disabled:active:scale-[0.97] disabled:cursor-default disabled:opacity-45',
                        )}
                        onClick={apply}
                        disabled={!canApply || draftTimeInvalid}
                      >
                        {t.apply}
                      </button>
                    </motion.div>
                  </div>
                  <p className="sr-only" aria-live="polite">
                    {status}
                  </p>
                </Panel>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </FilterField>
      {invalid && <p className="text-danger pb-2 text-xs">{t.dateRangeInvalid}</p>}
      {timeInvalid && <p className="text-danger pb-2 text-xs">{t.timeRangeInvalid}</p>}
    </div>
  )
}
